import type { EntityPluginContext } from "@brains/plugins";
import { z } from "@brains/utils/zod";
import { LinkAdapter } from "../adapters/link-adapter";
import { UrlUtils } from "./url-utils";
import { UrlFetcher } from "./url-fetcher";
import type { LinkSource, LinkStatus } from "../schemas/link";
import { readLinkStatus } from "../schemas/link";
import { linkExtractionSchema } from "../templates/extraction-template";

/**
 * Schema for link capture options
 */
export interface LinkCaptureOptionsMetadata {
  conversationId?: string | undefined;
  interfaceId?: string | undefined; // e.g. "cli", "matrix", "mcp"
  userId?: string | undefined;
  messageId?: string | undefined;
  timestamp?: string | undefined;
}

export interface LinkCaptureOptions {
  id?: string | undefined;
  metadata?: LinkCaptureOptionsMetadata | undefined;
}

const linkCaptureOptionsMetadataSchema: z.ZodType<LinkCaptureOptionsMetadata> =
  z.object({
    conversationId: z.string().optional(),
    interfaceId: z.string().optional(), // e.g. "cli", "matrix", "mcp"
    userId: z.string().optional(),
    messageId: z.string().optional(),
    timestamp: z.string().optional(),
  });

export const linkCaptureOptionsSchema: z.ZodType<LinkCaptureOptions> = z.object(
  {
    id: z.string().optional(),
    metadata: linkCaptureOptionsMetadataSchema.optional(),
  },
);

export interface LinkServiceOptions {
  /** Jina Reader API key for higher rate limits */
  jinaApiKey?: string;
}

/**
 * Core service for link operations
 */
export class LinkService {
  private context: EntityPluginContext;
  private linkAdapter: LinkAdapter;
  private urlFetcher: UrlFetcher;

  constructor(context: EntityPluginContext, options?: LinkServiceOptions) {
    this.context = context;
    this.linkAdapter = new LinkAdapter();
    this.urlFetcher = new UrlFetcher(
      options?.jinaApiKey ? { jinaApiKey: options.jinaApiKey } : undefined,
    );
  }

  /**
   * Resolve source from capture options
   */
  private async resolveSource(
    options?: LinkCaptureOptions,
  ): Promise<LinkSource> {
    const conversationId = options?.metadata?.conversationId;

    if (conversationId) {
      // Link captured from a conversation (Matrix, etc.)
      let label = conversationId;
      try {
        const conversation =
          await this.context.conversations.get(conversationId);
        label = conversation?.channelName ?? conversationId;
      } catch (error) {
        this.context.logger.debug("Could not resolve conversation metadata", {
          conversationId,
          error,
        });
      }
      return {
        ref: `matrix:${conversationId}`,
        label,
      };
    }

    // Manual addition (via MCP, CLI, etc.)
    const interfaceId = options?.metadata?.interfaceId ?? "cli";
    return {
      ref: `${interfaceId}:local`,
      label: interfaceId.toUpperCase(),
    };
  }

  /**
   * Capture a web link with AI extraction
   */
  async captureLink(
    url: string,
    options?: LinkCaptureOptions,
  ): Promise<{
    entityId: string;
    title: string;
    url: string;
    status: LinkStatus;
  }> {
    this.context.logger.debug("Starting link capture", { url });

    const entityId = options?.id ?? UrlUtils.generateEntityId(url);

    // Check for existing entity (deduplication)
    const existingEntity = await this.context.entityService.getEntity({
      entityType: "link",
      id: entityId,
    });
    if (existingEntity) {
      this.context.logger.info("Link already captured, returning existing", {
        url,
        entityId,
      });
      const { frontmatter } = this.linkAdapter.parseLinkContent(
        existingEntity.content,
      );
      return {
        entityId: existingEntity.id,
        title: frontmatter.title,
        url,
        status: readLinkStatus(existingEntity.metadata),
      };
    }

    // Fetch URL content
    this.context.logger.debug("Fetching URL content", { url });
    const fetchResult = await this.urlFetcher.fetch(url);

    if (!fetchResult.success) {
      if (
        fetchResult.errorType === "url_not_found" ||
        fetchResult.errorType === "url_unreachable"
      ) {
        this.context.logger.warn("Link URL not accessible", {
          url,
          errorType: fetchResult.errorType,
          error: fetchResult.error,
        });
        throw new Error(`Could not capture link: ${fetchResult.error}`);
      }
    }

    // Extract content with AI
    const extractionResult = await this.context.ai.generate(
      {
        templateName: "link:extraction",
        prompt: fetchResult.success
          ? `Extract structured information from this webpage content:\n\n${fetchResult.content}`
          : `The URL ${url} could not be fetched. Return success: false with error: "${fetchResult.error}"`,
        data: { url, hasContent: fetchResult.success },
        representedIdentity: "none",
        interfacePermissionGrant: "public",
      },
      linkExtractionSchema,
    );

    this.context.logger.debug("AI extraction result", {
      result: extractionResult,
    });

    const source = await this.resolveSource(options);
    const capturedAt = new Date().toISOString();

    // Handle extraction failure - save as pending
    if (
      extractionResult.success === false ||
      !extractionResult.title ||
      !extractionResult.description ||
      !extractionResult.summary
    ) {
      const title = extractionResult.title || new URL(url).hostname;

      this.context.logger.info("Incomplete extraction, saving as pending", {
        url,
      });

      const content = this.linkAdapter.createLinkContent({
        status: "pending",
        title,
        url,
        description: extractionResult.description,
        summary: extractionResult.summary,
        domain: new URL(url).hostname,
        capturedAt,
        source,
      });

      const entity = await this.context.entityService.createEntity({
        entity: {
          id: entityId,
          entityType: "link",
          content,
          metadata: { status: "pending", title, capturedAt },
        },
      });

      return {
        entityId: entity.entityId,
        title,
        url,
        status: "pending",
      };
    }

    // Complete extraction - save as draft
    const content = this.linkAdapter.createLinkContent({
      status: "draft",
      title: extractionResult.title,
      url,
      description: extractionResult.description,
      summary: extractionResult.summary,
      domain: new URL(url).hostname,
      capturedAt,
      source,
    });

    const entity = await this.context.entityService.createEntity({
      entity: {
        id: entityId,
        entityType: "link",
        content,
        metadata: {
          status: "draft",
          title: extractionResult.title,
          capturedAt,
        },
      },
    });

    return {
      entityId: entity.entityId,
      title: extractionResult.title,
      url,
      status: "draft",
    };
  }

  /**
   * List captured links
   */
  async listLinks(limit: number = 10): Promise<
    Array<{
      id: string;
      title: string;
      url: string;
      description?: string;
      domain: string;
      capturedAt: string;
    }>
  > {
    const results = await this.context.entityService.search({
      query: "",
      options: {
        types: ["link"],
        limit,
        sortBy: "created",
        sortDirection: "desc",
      },
    });

    return results.map((result) => {
      const { frontmatter } = this.linkAdapter.parseLinkContent(
        result.entity.content,
      );
      return {
        id: result.entity.id,
        title: frontmatter.title,
        url: frontmatter.url,
        ...(frontmatter.description && {
          description: frontmatter.description,
        }),
        domain: frontmatter.domain,
        capturedAt: frontmatter.capturedAt,
      };
    });
  }

  /**
   * Search captured links
   */
  async searchLinks(
    query?: string,
    limit: number = 20,
  ): Promise<
    Array<{
      id: string;
      title: string;
      url: string;
      description?: string;
      domain: string;
      capturedAt: string;
    }>
  > {
    const searchQuery = query ?? "";

    const results = await this.context.entityService.search({
      query: searchQuery,
      options: {
        types: ["link"],
        limit,
        sortBy: "created",
        sortDirection: "desc",
      },
    });

    return results.map((result) => {
      const { frontmatter } = this.linkAdapter.parseLinkContent(
        result.entity.content,
      );
      return {
        id: result.entity.id,
        title: frontmatter.title,
        url: frontmatter.url,
        ...(frontmatter.description && {
          description: frontmatter.description,
        }),
        domain: frontmatter.domain,
        capturedAt: frontmatter.capturedAt,
      };
    });
  }

  /**
   * Get a specific link by ID
   */
  async getLink(linkId: string): Promise<{
    id: string;
    title: string;
    url: string;
    description?: string;
    summary?: string;
    domain: string;
    capturedAt: string;
    status: LinkStatus;
  } | null> {
    const entity = await this.context.entityService.getEntity({
      entityType: "link",
      id: linkId,
    });
    if (!entity) {
      return null;
    }

    const { frontmatter, summary } = this.linkAdapter.parseLinkContent(
      entity.content,
    );
    return {
      id: entity.id,
      title: frontmatter.title,
      url: frontmatter.url,
      ...(frontmatter.description && { description: frontmatter.description }),
      ...(summary && { summary }),
      domain: frontmatter.domain,
      capturedAt: frontmatter.capturedAt,
      status: readLinkStatus(entity.metadata),
    };
  }
}
